"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Mail,
  MessageCircle,
  Send,
  HelpCircle,
} from "lucide-react";
import { useAuth } from "@/lib/hooks/useAuth";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { AppPageShell } from "@/components/layout/AppPageShell";
import {
  getSupportCategoriesFor,
  SUPPORT_EMAIL,
  SUPPORT_HOURS_LABEL,
  SUPPORT_KAKAO_HANDLE,
  SUPPORT_KAKAO_URL,
  SUPPORT_PLATFORM_NOTICE,
  type SupportAudience,
} from "@/lib/config/customerSupport";

interface ContactFormData {
  name: string;
  email: string;
  category: string;
  subject: string;
  message: string;
}

export default function ContactForm() {
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const audienceParam = searchParams.get("audience");
  const categoryParam = searchParams.get("category");
  const subjectParam = searchParams.get("subject");
  const audience: SupportAudience =
    audienceParam === "pro" ? "pro" : "customer";

  const categories = useMemo(
    () => getSupportCategoriesFor(audience),
    [audience]
  );

  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    category: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      name:
        prev.name ||
        String(
          user?.nickname ||
            (user as { displayName?: string } | null)?.displayName ||
            user?.email?.split("@")[0] ||
            ""
        ),
      email: prev.email || user?.email || "",
      category:
        categoryParam && categories.some(c => c.value === categoryParam)
          ? categoryParam
          : prev.category && categories.some(c => c.value === prev.category)
            ? prev.category
            : "",
      subject: subjectParam?.trim() || prev.subject,
    }));
  }, [user, categoryParam, categories, subjectParam]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          userId: user?.uid,
          audience,
        }),
      });

      if (!response.ok) throw new Error("문의 전송에 실패했습니다.");

      setSubmitStatus("success");
      setFormData({
        name: String(
          user?.nickname ||
            (user as { displayName?: string } | null)?.displayName ||
            user?.email?.split("@")[0] ||
            ""
        ),
        email: user?.email || "",
        category: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("문의 전송 오류:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppPageShell
      title={audience === "pro" ? "프로 지원 1:1 문의" : "고객센터 1:1 문의"}
      description={
        audience === "pro"
          ? "정산·출금·배정·프로필 관련 문의를 남겨 주세요."
          : "결제·환불·피드백·레슨 등 운영팀 확인이 필요한 내용을 남겨 주세요."
      }
      maxWidth="xl"
    >
      <div className="mb-5 flex flex-wrap gap-2">
        <Link
          href="/contact?audience=customer"
          className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
            audience === "customer"
              ? "bg-violet-600 text-white"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          고객 문의
        </Link>
        <Link
          href="/contact?audience=pro"
          className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
            audience === "pro"
              ? "bg-teal-700 text-white"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          프로 지원
        </Link>
        <Link
          href="/profile/inquiries"
          className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-200"
        >
          내 문의함
        </Link>
      </div>

      <p className="mb-6 flex items-start gap-2 text-xs leading-relaxed text-gray-500">
        <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          상담 시간 {SUPPORT_HOURS_LABEL}. {SUPPORT_PLATFORM_NOTICE}
        </span>
      </p>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          <Card className="border border-gray-200 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
              <div>
                <h3 className="mb-1 font-medium text-gray-900">이메일</h3>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="break-all text-sm text-gray-600 transition-colors hover:text-violet-600"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </Card>

          <Card className="border border-gray-200 p-5 shadow-sm">
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-gray-700" />
                <div>
                  <h3 className="mb-1 font-medium text-gray-900">카카오톡</h3>
                  <p className="text-sm text-gray-600">{SUPPORT_KAKAO_HANDLE}</p>
                  <p className="mt-1 text-xs text-gray-500">빠른 상담</p>
                </div>
              </div>
              <a
                href={SUPPORT_KAKAO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#FEE500] px-4 py-2.5 text-sm font-medium text-[#191919] transition-colors hover:bg-[#F5DC00]"
              >
                카카오톡 문의하기
              </a>
            </div>
          </Card>

          <Card className="border border-gray-200 bg-gray-50 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
              <div>
                <h3 className="mb-1 font-medium text-gray-900">빠른 도움말</h3>
                <p className="mb-3 text-sm text-gray-600">
                  FAQ에서 먼저 답을 찾아보세요.
                </p>
                <Link href="/faq">
                  <Button variant="outline" size="sm" className="w-full">
                    자주 묻는 질문
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="border border-gray-200 p-6 shadow-sm sm:p-8">
            {submitStatus === "success" ? (
              <div className="py-10 text-center">
                <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-600" />
                <h2 className="mb-2 text-xl font-semibold text-gray-900">
                  문의가 접수되었습니다
                </h2>
                <p className="mb-6 text-sm text-gray-600">
                  운영팀이 확인 후 답변드립니다.
                  <br />
                  로그인 상태라면 내 문의함에서 처리 상태를 볼 수 있습니다.
                </p>
                <div className="flex justify-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setSubmitStatus("idle")}
                  >
                    새 문의 작성
                  </Button>
                  <Link href="/profile/inquiries">
                    <Button variant="primary">내 문의함</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      이름 *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-violet-500"
                      placeholder="홍길동"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">
                      이메일 *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-violet-500"
                      placeholder="example@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    문의 유형 *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="">선택해주세요</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    제목 *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-violet-500"
                    placeholder="문의 제목을 입력해주세요"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    문의 내용 *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={8}
                    className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-transparent focus:ring-2 focus:ring-violet-500"
                    placeholder="문의하실 내용을 자세히 작성해주세요. 주문번호·결제일 등이 있으면 함께 적어 주세요."
                  />
                </div>

                {submitStatus === "error" && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
                    <p className="text-sm text-red-700">
                      문의 전송에 실패했습니다. 잠시 후 다시 시도해주세요.
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-gray-500">
                    * 표시는 필수 입력 항목입니다.
                  </p>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                    className="min-w-[120px]"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                        전송 중...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Send className="mr-2 h-4 w-4" />
                        문의하기
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </div>
    </AppPageShell>
  );
}
